import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { WishlistHeart } from "../components/wishlist/WishlistHeart";
import { PageHeader } from "../components/storefront/PageHeader";
import { ProductImage } from "../components/ProductCard";
import { Button, PageLoader } from "../components/ui";
import { trackMarketplaceListingImpression } from "../lib/marketplaceImpressions";
import { marketplaceShopPath } from "../lib/marketplaceShopPath";
import { ListingReviewsPanel } from "../components/reviews/ProductReviewsPanel";
import { FlexxiMarketplacePanel } from "../components/marketplace/FlexxiMarketplacePanel";
import { StarRating } from "../components/reviews/StarRating";

type ListingDetail = {
  id: string;
  title: string;
  description: string | null;
  price: number;
  quantity: number;
  images: string[];
  seller: { shopName: string; shopSlug: string; id: string };
  avgRating?: number;
  ratingCount?: number;
  soldCount?: number;
};

export function MarketDetail() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const shop = sp.get("shop") ?? undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, config } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["market-listing", slug, shop],
    queryFn: () =>
      api<{ item: ListingDetail }>(`/marketplace/listings/${slug}${shop ? `?shop=${shop}` : ""}`),
  });

  const item = data?.item;
  const maxQty = item?.quantity ?? 1;

  useEffect(() => {
    if (item?.id) trackMarketplaceListingImpression(item.id, "DETAIL");
  }, [item?.id]);

  async function addToCart(buyNow = false) {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!item) return;
    setBusy(true);
    try {
      await api("/marketplace/cart/items", {
        method: "POST",
        body: { listingId: item.id, quantity },
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["cart"] }),
        qc.invalidateQueries({ queryKey: ["market-cart"] }),
      ]);
      if (buyNow) {
        const params = new URLSearchParams({ sellers: item.seller.id, listings: item.id });
        navigate(`/checkout?${params.toString()}`);
      } else {
        toast("success", "Added to cart");
      }
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't add to cart");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <PageLoader />;
  if (!item) return <p className="p-8 text-muted">Listing not found</p>;

  const soldOut = maxQty < 1;

  return (
    <div className="space-y-4 px-4 pt-4 lg:px-6">
      <PageHeader
        title={item.title}
        description={
          <Link to={marketplaceShopPath(item.seller.shopSlug)} className="text-brand-pink hover:underline">
            {item.seller.shopName}
          </Link>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <div className="relative overflow-hidden rounded-3xl border border-white/5">
          <ProductImage src={item.images[0] ?? null} alt={item.title} className="aspect-square w-full" />
          <div className="absolute right-3 top-3 z-10">
            <WishlistHeart kind="MARKETPLACE" listingId={item.id} />
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <p className="font-display text-2xl font-bold text-brand-pink">{rm(item.price)}</p>
            <p className="mt-1 text-sm text-white/50">
              {maxQty} available
              {(item.soldCount ?? 0) > 0 ? ` · ${item.soldCount} sold` : ""}
            </p>
            {(item.ratingCount ?? 0) > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <StarRating value={Math.round(item.avgRating ?? 0)} readOnly size={16} />
                <span className="text-xs text-white/50">
                  {(item.avgRating ?? 0).toFixed(1)} ({item.ratingCount} reviews)
                </span>
              </div>
            )}
            {item.description && <p className="mt-4 text-sm leading-relaxed text-white/70">{item.description}</p>}
          </div>

          {!soldOut ? (
            <div className="space-y-3 lg:max-w-md">
              <div className="flex items-center gap-3">
                <div className="flex items-center rounded-xl bg-white/5">
                  <button
                    type="button"
                    className="px-3.5 py-2 text-lg"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  >
                    −
                  </button>
                  <span className="w-8 text-center font-display text-sm">{quantity}</span>
                  <button
                    type="button"
                    className="px-3.5 py-2 text-lg"
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  >
                    +
                  </button>
                </div>
                <span className="text-xs text-white/45">Max {maxQty}</span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button className="flex-1" disabled={busy} onClick={() => void addToCart(false)}>
                  Add to cart
                </Button>
                <Button variant="outline" className="flex-1" disabled={busy} onClick={() => void addToCart(true)}>
                  Buy now
                </Button>
              </div>
              {config?.flexxiMarketplaceEnabled ? (
                <FlexxiMarketplacePanel listingId={item.id} quantity={quantity} />
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-amber-300/90">This listing is sold out.</p>
          )}
        </div>
      </div>

      <section className="border-t border-white/5 pt-6">
        <h2 className="mb-3 font-display text-sm font-semibold text-white">Reviews</h2>
        <ListingReviewsPanel listingId={item.id} />
      </section>
    </div>
  );
}
